import { ReactNode } from 'react';
import { IControlProps, IPartialFormGroup } from '../types';
import FormControl from '../FormControl';
import DateTimeField, { IDateTimeFieldProps } from '../../atoms/DateTimeField';
import { useHookFormController } from '../../hooks/useHookFormController';
import { dateToIsoDateTime, isoToDate } from '../../lib/dateFormValue';

export type IDateTimeFieldControlProps = Omit<IDateTimeFieldProps, 'value' | 'onChange' | 'defaultValue'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        onValueChange?: (value: Date | null) => void;
        isValueAsString?: boolean;
        /** Очищенное поле — `undefined` вместо `null`: ключ не попадает в JSON (так формы админки отдавали дату серверу) */
        clearToUndefined?: boolean;
    };

const DateTimeFieldControl = ({
    name,
    rules,
    defaultValue,
    control,
    shouldUnregister,
    label,
    tooltip,
    required,
    formGroupProps,
    hideErrorMessage,
    onValueChange,
    isValueAsString,
    clearToUndefined,
    readOnly,
    'aria-label': ariaLabel,
    ...props
}: IDateTimeFieldControlProps) => {
    const Controller = useHookFormController();

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            render={({ field: { onChange, value, ...fieldProps } }) => {
                // В isValueAsString-режиме значение приходит строкой (в т.ч. RU-формат
                // «ДД.ММ.ГГГГ ЧЧ:ММ» от ToString() на сервере). Нормализуем через safeDate,
                // чтобы пикер показал и не-ISO ввод; нераспознанное оставляем как есть.
                const currentIso = isValueAsString
                    ? (dateToIsoDateTime(value) ?? (typeof value === 'string' ? value : null))
                    : dateToIsoDateTime(value);

                return (
                    <FormControl
                        readonlyAppearance={readOnly || props.disabled ? 'datetime' : undefined}
                        name={name}
                        label={label}
                        tooltip={tooltip}
                        required={required}
                        control={control}
                        hideErrorMessage={hideErrorMessage}
                        {...formGroupProps}
                    >
                        <DateTimeField
                            {...fieldProps}
                            {...props}
                            readOnly={readOnly}
                            // без своего имени поле названо подписью группы (FormGroup)
                            aria-label={ariaLabel}
                            value={currentIso}
                            onChange={(next) => {
                                if (readOnly) return;
                                const iso = typeof next === 'string' ? next : null;
                                if (iso === currentIso) return;
                                const nextDate = isoToDate(iso);
                                if (iso === null && clearToUndefined) {
                                    onChange(undefined);
                                } else if (isValueAsString) {
                                    onChange(iso);
                                } else {
                                    onChange(nextDate);
                                }
                                if (onValueChange) onValueChange(nextDate);
                            }}
                        />
                    </FormControl>
                );
            }}
        />
    );
};

export default DateTimeFieldControl;
